import type { SupClaimableReward } from "@/hooks/rewards/useProtocolRewardsClaimable";
import React, { useCallback, useMemo, useRef, useState } from "react";

import Image from "next/image";
import useSupportedChains from "@/hooks/api/useSupportedChains";
import { useClaimRewardsStore } from "@/zstnd/providers/claim-rewards-store-provider";
import { useStoredDataDispatch } from "@/store";

import { SupButton, SupPopover, SupText } from "@repo/core-ui";

interface PortfolioRewardsAtomClaimProps {
  rewardsClaimable?: SupClaimableReward[] | null;
  isLoading?: boolean;
}

export default function PortfolioRewardsAtomClaim({ rewardsClaimable, isLoading = false }: PortfolioRewardsAtomClaimProps) {
  const storedDispatch = useStoredDataDispatch();
  const { data: supportedChainsData, isLoading: isLoadingChainsData } = useSupportedChains();

  const { setChainId } = useClaimRewardsStore(state => state);

  const buttonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const handleClose = () => setAnchorEl(null);

  const handleOpenPopover = useCallback(() => {
    setAnchorEl(anchorEl ? null : buttonRef.current);
  }, [anchorEl, buttonRef]);

  const disabled = useMemo(() => (rewardsClaimable?.length ?? 0) < 1 || isLoading || isLoadingChainsData, [rewardsClaimable?.length, isLoading, isLoadingChainsData]);

  const onClickHandler = useCallback(
    (chainId: number) => {
      setChainId(chainId);
      handleClose();
      storedDispatch({ type: "setOverlay", overlay: "claim-rewards" });
    },
    [setChainId, storedDispatch]
  );

  const chainList = useMemo(
    () =>
      (rewardsClaimable ?? []).reduce<{ chain_id: number; icon: string; name: string }[]>((acc, reward) => {
        if (acc.some(item => item.chain_id === reward.chain_id)) {
          return acc;
        }
        const chainData = supportedChainsData?.find(item => item.chain_id === reward.chain_id);
        return [
          ...acc,
          {
            chain_id: reward.chain_id,
            icon: chainData?.icon ?? "",
            name: chainData?.name ?? "",
          },
        ];
      }, []),
    [rewardsClaimable, supportedChainsData]
  );

  return (
    <div className="relative inline-flex items-center">
      <SupButton textClassName="text-[12px]" ref={buttonRef} disabled={disabled} className="h-6 whitespace-nowrap bg-primary-800 p-1" onClick={handleOpenPopover}>
        Claim Rewards
      </SupButton>
      <SupPopover portal anchorEl={anchorEl} open={open} onClickOutside={handleClose} offset={[0, 4]} className="layout-menu-account h-fit border-0.5 border-neutral-600 p-0">
        <nav className="mt-auto flex w-fit flex-col justify-center space-y-1 whitespace-nowrap">
          {chainList?.map(chainItem => (
            <SupButton
              key={`claimable-action-for-chain-${chainItem.chain_id}`}
              onClick={() => onClickHandler(chainItem.chain_id)}
              variant="text"
              className="txt-16_24 flex h-12 items-center p-2 duration-100 hover:ease-in md:hover:bg-primary-75 md:hover:text-neutral-700 lg:flex"
            >
              <SupText variant="subtitle1" className="flex items-center justify-between space-x-2 text-neutral-950">
                <span>Claim on {chainItem.name}</span>
                <Image unoptimized={true} width={24} height={24} src={chainItem.icon} alt={chainItem.name} />
              </SupText>
            </SupButton>
          ))}
        </nav>
      </SupPopover>
    </div>
  );
}
